import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─── tiny keyframe injection ─────────────────────────────────────────────── */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
@keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-18px)} }
@keyframes pulse-ring { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(1.6);opacity:0} }
@keyframes gradient-shift {
  0%{background-position:0% 50%}
  50%{background-position:100% 50%}
  100%{background-position:0% 50%}
}
@keyframes slide-up { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
@keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
.lp * { box-sizing: border-box; }
.lp { font-family: 'Inter', system-ui, sans-serif; }
.hero-text {
  background: linear-gradient(135deg, #fff 0%, #e0e7ff 40%, #c7d2fe 70%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.glow-btn {
  background: linear-gradient(135deg, #6366f1, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  animation: gradient-shift 3s ease infinite;
  box-shadow: 0 0 32px rgba(139,92,246,0.5), 0 4px 16px rgba(99,102,241,0.4);
  transition: transform .2s, box-shadow .2s;
}
.glow-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 0 48px rgba(139,92,246,0.7), 0 8px 24px rgba(99,102,241,0.5);
}
.feature-card {
  transition: transform .25s, box-shadow .25s;
}
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 48px rgba(99,102,241,0.2);
}
.step-line::after {
  content: '';
  position: absolute;
  top: 24px; left: 100%;
  width: calc(100% - 48px);
  height: 2px;
  background: linear-gradient(90deg, #6366f1, transparent);
}
`;

/* ─── mock dashboard screenshot ────────────────────────────────────────────── */
function DashboardMockup() {
  return (
    <div style={{
      width: '100%', maxWidth: 820,
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 40px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
      background: '#0f172a',
    }}>
      {/* Browser chrome */}
      <div style={{ background: '#1e293b', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
        <div style={{
          flex: 1, margin: '0 12px', background: '#334155', borderRadius: 6,
          padding: '3px 10px', fontSize: 11, color: '#64748b',
        }}>praveda.btrix.in</div>
      </div>

      {/* App UI */}
      <div style={{ display: 'flex', height: 380 }}>
        {/* Sidebar */}
        <div style={{ width: 180, background: '#0f172a', padding: '16px 0', borderRight: '1px solid #1e293b', flexShrink: 0 }}>
          <div style={{ padding: '0 14px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 800 }}>P</div>
            <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 700 }}>Praveda</span>
          </div>
          {[
            { label: 'Dashboard', icon: '⬛', active: true },
            { label: 'Patients', icon: '⬜', active: false },
            { label: 'Appointments', icon: '⬜', active: false },
            { label: 'Notifications', icon: '⬜', active: false },
            { label: 'Staff', icon: '⬜', active: false },
          ].map((item, i) => (
            <div key={i} style={{
              padding: '8px 14px', margin: '1px 8px', borderRadius: 8,
              background: item.active ? 'rgba(99,102,241,0.2)' : 'transparent',
              borderLeft: item.active ? '3px solid #6366f1' : '3px solid transparent',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: item.active ? '#6366f1' : '#334155' }} />
              <span style={{ fontSize: 11, color: item.active ? '#a5b4fc' : '#475569', fontWeight: item.active ? 600 : 400 }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Main */}
        <div style={{ flex: 1, background: '#f8fafc', overflow: 'hidden' }}>
          {/* Topbar */}
          <div style={{ background: '#fff', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Dashboard</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }} />
              <span style={{ fontSize: 11, color: '#64748b' }}>Dr. Admin</span>
            </div>
          </div>

          <div style={{ padding: 14 }}>
            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
              {[
                { label: 'Patients', val: '1,248', color: '#6366f1' },
                { label: 'Appointments', val: '86', color: '#8b5cf6' },
                { label: 'Messages', val: '5,320', color: '#ec4899' },
                { label: 'Staff', val: '34', color: '#0ea5e9' },
              ].map((s, i) => (
                <div key={i} style={{
                  background: '#fff', borderRadius: 10, padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Table mock */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#475569' }}>Recent Patients</div>
              {[
                { name: 'Ramesh Kumar', status: 'Confirmed', color: '#dcfce7', tc: '#15803d' },
                { name: 'Priya Sharma', status: 'Pending', color: '#fef9c3', tc: '#854d0e' },
                { name: 'Arjun Reddy', status: 'Discharged', color: '#f1f5f9', tc: '#475569' },
                { name: 'Sita Devi', status: 'Confirmed', color: '#dcfce7', tc: '#15803d' },
              ].map((r, i) => (
                <div key={i} style={{ padding: '6px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', fontSize: 9, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{r.name[0]}</div>
                    <span style={{ fontSize: 11, color: '#1e293b' }}>{r.name}</span>
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 600, background: r.color, color: r.tc, padding: '2px 7px', borderRadius: 20 }}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── main component ────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <>
      <style>{STYLES}</style>
      <div className="lp" style={{ background: '#0f0c29', overflowX: 'hidden' }}>

        {/* ── NAV ── */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 40px',
          background: scrolled ? 'rgba(15,12,41,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(99,102,241,0.2)' : '1px solid transparent',
          transition: 'all .3s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 800, fontSize: 17,
            }}>P</div>
            <span style={{ fontWeight: 800, fontSize: 17, color: '#fff' }}>Praveda</span>
          </div>
          <button className="glow-btn" onClick={() => navigate('/login')}
            style={{ padding: '8px 24px', borderRadius: 8, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Sign In →
          </button>
        </nav>

        {/* ── HERO ── */}
        <section style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '80px 24px 60px',
          position: 'relative', overflow: 'hidden', textAlign: 'center',
        }}>
          {/* animated blobs */}
          {[
            { w: 500, h: 500, top: -150, left: -100, c: 'rgba(99,102,241,0.25)', delay: '0s' },
            { w: 400, h: 400, top: '30%', right: -120, c: 'rgba(139,92,246,0.2)', delay: '1s' },
            { w: 300, h: 300, bottom: -80, left: '40%', c: 'rgba(236,72,153,0.15)', delay: '2s' },
          ].map((b, i) => (
            <div key={i} style={{
              position: 'absolute', width: b.w, height: b.h, borderRadius: '50%',
              background: `radial-gradient(circle, ${b.c} 0%, transparent 70%)`,
              top: b.top, left: b.left, right: b.right, bottom: b.bottom,
              animation: `float 6s ease-in-out ${b.delay} infinite`,
              pointerEvents: 'none',
            }} />
          ))}

          {/* pill badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)',
            borderRadius: 24, padding: '5px 16px', marginBottom: 28, position: 'relative',
            animation: 'slide-up .6s ease both',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#818cf8', display: 'inline-block', position: 'relative' }}>
              <span style={{
                position: 'absolute', inset: 0, borderRadius: '50%', background: '#818cf8',
                animation: 'pulse-ring .9s ease-out infinite',
              }} />
            </span>
            <span style={{ color: '#a5b4fc', fontSize: 12, fontWeight: 600 }}>Hospital SaaS Platform · by Btrix Technologies</span>
          </div>

          {/* headline */}
          <h1 className="hero-text" style={{
            fontSize: 'clamp(40px, 7vw, 80px)', fontWeight: 900, lineHeight: 1.05,
            margin: '0 0 24px', maxWidth: 820,
            animation: 'slide-up .7s ease .1s both',
          }}>
            The Future of<br />Hospital Management
          </h1>

          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)', color: '#94a3b8', lineHeight: 1.7,
            maxWidth: 560, margin: '0 0 44px',
            animation: 'slide-up .7s ease .2s both',
          }}>
            Automate WhatsApp reminders, manage patients across multiple hospitals, and keep your entire team in sync — all from one powerful dashboard.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', animation: 'slide-up .7s ease .3s both' }}>
            <button className="glow-btn" onClick={() => navigate('/login')}
              style={{ padding: '14px 36px', borderRadius: 10, color: '#fff', border: 'none', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Enter Dashboard →
            </button>
            <a href="#how" style={{
              padding: '14px 32px', borderRadius: 10, fontSize: 15, fontWeight: 600,
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
              color: '#e2e8f0', textDecoration: 'none', cursor: 'pointer',
              transition: 'background .2s',
            }}
              onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.13)'}
              onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.07)'}
            >
              See how it works
            </a>
          </div>

          {/* mockup */}
          <div style={{
            marginTop: 72, position: 'relative',
            animation: 'slide-up .8s ease .4s both',
          }}>
            <DashboardMockup />
            {/* glow under mockup */}
            <div style={{
              position: 'absolute', bottom: -40, left: '50%', transform: 'translateX(-50%)',
              width: '70%', height: 60,
              background: 'radial-gradient(ellipse, rgba(99,102,241,0.5) 0%, transparent 70%)',
              filter: 'blur(20px)',
              pointerEvents: 'none',
            }} />
          </div>
        </section>

        {/* ── TICKER ── */}
        <div style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', padding: '14px 0', overflow: 'hidden' }}>
          <div style={{ display: 'flex', animation: 'ticker 20s linear infinite', whiteSpace: 'nowrap' }}>
            {[...Array(2)].map((_, rep) => (
              <span key={rep} style={{ display: 'inline-flex', gap: 0 }}>
                {['Patient Management', 'WhatsApp Notifications', 'Multi-Hospital', 'Role-Based Access', 'Appointment Scheduling', 'Audit Logs', 'Staff Management', 'Lab Reports'].map((t, i) => (
                  <span key={i} style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '0 32px' }}>
                    ✦ {t}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <section id="how" style={{ padding: '100px 24px', background: 'linear-gradient(180deg, #13102b 0%, #1a1040 100%)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at 20% 50%, rgba(139,92,246,0.1) 0%, transparent 60%)' }} />
          <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: 64 }}>
              <div style={{
                display: 'inline-block', padding: '4px 16px', borderRadius: 20,
                background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                color: '#a5b4fc', fontSize: 12, fontWeight: 700, marginBottom: 16,
                textTransform: 'uppercase', letterSpacing: 1,
              }}>How It Works</div>
              <h2 style={{ fontSize: 'clamp(28px,5vw,44px)', fontWeight: 900, color: '#fff', margin: '0 0 14px' }}>
                Up and running in minutes
              </h2>
              <p style={{ fontSize: 16, color: '#64748b', maxWidth: 480, margin: '0 auto' }}>
                Three simple steps to streamline your hospital operations.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
              {[
                {
                  step: '01',
                  title: 'Set up your hospital',
                  desc: 'Add your hospital, configure departments, and invite your team with the right roles in just a few clicks.',
                  gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  border: 'rgba(99,102,241,0.3)',
                },
                {
                  step: '02',
                  title: 'Register patients',
                  desc: 'Reception staff register patients, log appointments, and attach lab reports or OP sheets seamlessly.',
                  gradient: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  border: 'rgba(139,92,246,0.3)',
                },
                {
                  step: '03',
                  title: 'Automate notifications',
                  desc: 'Praveda sends WhatsApp reminders for appointments, discharge summaries, and follow-ups — automatically.',
                  gradient: 'linear-gradient(135deg, #ec4899, #f97316)',
                  border: 'rgba(236,72,153,0.3)',
                },
              ].map((s, i) => (
                <div key={i} className="feature-card" style={{
                  background: 'rgba(255,255,255,0.04)', borderRadius: 20, padding: '32px 28px',
                  border: `1px solid ${s.border}`,
                  backdropFilter: 'blur(8px)',
                }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 48, height: 48, borderRadius: 14,
                    background: s.gradient, color: '#fff',
                    fontWeight: 900, fontSize: 16, marginBottom: 20,
                  }}>{s.step}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: '#e2e8f0', margin: '0 0 10px' }}>{s.title}</h3>
                  <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURES ── */}
        <section style={{ padding: '80px 24px', background: 'linear-gradient(180deg, #1a1040 0%, #0f0c29 100%)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at 80% 30%, rgba(236,72,153,0.1) 0%, transparent 55%)' }} />
          <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <div style={{
                display: 'inline-block', padding: '4px 16px', borderRadius: 20,
                background: 'rgba(236,72,153,0.15)', border: '1px solid rgba(236,72,153,0.3)',
                color: '#f9a8d4', fontSize: 12, fontWeight: 700, marginBottom: 16,
                textTransform: 'uppercase', letterSpacing: 1,
              }}>Features</div>
              <h2 style={{ fontSize: 'clamp(28px,5vw,44px)', fontWeight: 900, color: '#fff', margin: '0 0 14px' }}>
                Everything in one place
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              {[
                { icon: '💬', title: 'WhatsApp Notifications', desc: 'Send automated appointment reminders and health updates to patients via WhatsApp.', grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'rgba(99,102,241,0.25)' },
                { icon: '🏥', title: 'Multi-Hospital Support', desc: 'Manage multiple hospitals from one account with complete tenant isolation and per-hospital analytics.', grad: 'linear-gradient(135deg,#8b5cf6,#ec4899)', border: 'rgba(139,92,246,0.25)' },
                { icon: '👥', title: 'Patient Records', desc: 'Full patient history, OP sheets, lab reports, and visit logs — all searchable and linked to appointments.', grad: 'linear-gradient(135deg,#ec4899,#f97316)', border: 'rgba(236,72,153,0.25)' },
                { icon: '📅', title: 'Appointment Management', desc: 'Book, reschedule, and track appointments with real-time status updates for doctors and reception staff.', grad: 'linear-gradient(135deg,#f97316,#eab308)', border: 'rgba(249,115,22,0.25)' },
                { icon: '🔐', title: 'Role-Based Access', desc: 'Super Admin, Hospital Admin, Reception, and Doctor — each sees exactly what they need, nothing more.', grad: 'linear-gradient(135deg,#0ea5e9,#6366f1)', border: 'rgba(14,165,233,0.25)' },
                { icon: '📊', title: 'Audit & Compliance', desc: 'Every action is logged. Audit trails keep your hospital compliant and give you full operational visibility.', grad: 'linear-gradient(135deg,#22c55e,#0ea5e9)', border: 'rgba(34,197,94,0.25)' },
              ].map((f, i) => (
                <div key={i} className="feature-card" style={{
                  background: 'rgba(255,255,255,0.04)', borderRadius: 18, padding: '28px 24px',
                  border: `1px solid ${f.border}`,
                  backdropFilter: 'blur(8px)',
                  display: 'flex', flexDirection: 'column', gap: 14,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 16,
                    background: f.grad,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24,
                  }}>{f.icon}</div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#e2e8f0', marginBottom: 6 }}>{f.title}</div>
                    <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section style={{
          padding: '100px 24px',
          background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)',
          textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse at 50% 50%, rgba(99,102,241,0.25) 0%, transparent 70%)',
          }} />
          <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(30px,5vw,52px)', fontWeight: 900, color: '#fff',
              margin: '0 0 18px', lineHeight: 1.1,
            }}>
              Ready to transform your hospital?
            </h2>
            <p style={{ fontSize: 17, color: '#94a3b8', margin: '0 0 40px', lineHeight: 1.6 }}>
              Join hospitals across India using Praveda to deliver better patient experiences.
            </p>
            <button className="glow-btn" onClick={() => navigate('/login')}
              style={{ padding: '16px 48px', borderRadius: 12, color: '#fff', border: 'none', fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>
              Sign In to Praveda →
            </button>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer style={{
          background: '#0a0a0f', padding: '24px 40px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, borderRadius: 7,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 12, fontWeight: 800,
            }}>P</div>
            <span style={{ color: '#64748b', fontSize: 13 }}>
              Praveda · <span style={{ color: '#818cf8' }}>Btrix Technologies</span>
            </span>
          </div>
          <span style={{ color: '#334155', fontSize: 12 }}>© 2025 All rights reserved.</span>
        </footer>

      </div>
    </>
  );
}
